import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
} from "react";
import {
  Box,
  Typography,
  Container,
  CircularProgress,
  useTheme,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  Link,
  FormControl,
  Grid,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import moment from "moment";
import { isObject } from "lodash";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { setStoreData as setStore } from "../redux/action";
import { getAPI, postAPI } from "../api/callAPI";

import NotFound from "../Common/NotFound";
import { parsePhoneNumber } from "libphonenumber-js";
import { initPhone } from "../Common/utils";
import { useSnackbar } from "../Common/useSnackbar";
import PhoneNumberInput, {
  isValidPhoneNumber,
} from "../Common/PhoneNumberInput";

initPhone();

const Count = Array.from({ length: 31 }, (_, i) => i);

const SpeedBooking = () => {
  const { palette } = useTheme();
  // const eventsRef = useRef(null);
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const location = useLocation();
  const dispatch = useDispatch();

  // reCAPTCHA removed as OTP is used

  const { endpoint, store } = useSelector((state) => state.auth);

  const [storeLoading, setStoreLoading] = useState(true);
  const [storeData, setStoreData] = useState(null);
  const [error, setError] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [coverCharges, setCoverCharges] = useState([]);
  const [coverChargesLoading, setCoverChargesLoading] = useState(false);
  const [prefillLoading, setPrefillLoading] = useState(false);

  const storeID = useMemo(() => {
    if (location.pathname) {
      return location.pathname.replace("/speed-booking/", "");
    }
    return null;
  }, [location.pathname]);

  const isCoverBooking = useMemo(() => {
    return !coverChargesLoading && coverCharges?.length > 0;
  }, [coverChargesLoading, coverCharges]);

  // console.log("otp_enable >> ", store?.otp_enable);

  const validationSchema = useMemo(() => {
    return Yup.object({
      firstName: Yup.string().required("First name is required"),
      lastName: Yup.string().optional(),
      city: Yup.string().required("City is required"),
      guests: Yup.string()
        .required("Number of guests is required")
        .min(1, "At least 1 guest required")
        .max(30, "Max 30 guests allowed"),
      mobile: Yup.string()
        .required("Mobile number is required")
        .test("is-valid-phone", "Invalid phone number", (value) =>
          isValidPhoneNumber(value || "")
        ),
      otp: store?.otp_enable && otpSent
        ? Yup.string().required("OTP is required")
        : Yup.string().optional(),
      agreement: Yup.boolean().oneOf(
        [true],
        "You must accept the Terms and Privacy Policy"
      ),
    });
  }, [store?.otp_enable, otpSent]);

  const { termsURL, privacyURL } = useMemo(() => {
    return {
      termsURL: `https://${endpoint}.feedthetummy.com/page/terms`,
      privacyURL: `https://${endpoint}.feedthetummy.com/page/privacy-policy`,
    };
  }, [endpoint]);

  const checkStore = useCallback(async () => {
    if (storeID) {
      dispatch(setStore({}));
      setStoreLoading(true);
      try {
        const resp = await getAPI(endpoint, `stores/${storeID}`);
        // console.log("resp >>> ", resp);
        dispatch(setStore(resp?.data["0"]));
        setStoreData(resp?.data["0"]);
      } catch (error) {
        console.error("checkStore error >>> ", error);
        dispatch(setStore({}));
        setStoreData(null);
        setError("Store Not Found");
      }
      setStoreLoading(false);
    } else {
      setStoreData(null);
      dispatch(setStore({}));
      setError("Store Not Found");
      setStoreLoading(false);
    }
  }, [storeID, dispatch, endpoint]);

  useEffect(() => {
    checkStore();
  }, [checkStore]);

  useEffect(() => {
    const fetchCoverCharges = async () => {
      if (!storeID) return;
      setCoverChargesLoading(true);
      try {
        const resp = await postAPI(endpoint, "store_cover_charge", {
          store_id: parseInt(storeID, 10),
        });

        // console.log("fetchCoverCharges resp >>> ", resp);
        const data = resp?.data ?? resp; // be lenient with API shape
        let normalized = [];
        if (Array.isArray(data) && data.length > 0) {
          const first = data[0] || {};
          if (first.entry_fee !== undefined && first.entry_fee !== null) {
            normalized.push({ title: "Entry Fee (Per Person)", price: first.entry_fee });
          }
          if (first.cover_charge !== undefined && first.cover_charge !== null) {
            normalized.push({ title: "Cover Charge (Per Table)", price: first.cover_charge });
          }
        }
        setCoverCharges(normalized);
      } catch (e) {
        // fail silently; not critical
        setCoverCharges([]);
      } finally {
        setCoverChargesLoading(false);
      }
    };
    fetchCoverCharges();
  }, [endpoint, storeID]);

  // const onSubmit = (values) => {
  //   console.log("Form data:", values);
  //   // alert("Form submitted!");
  //   const reqData = {
  //     adult: values.guests,
  //     store_id: storeID,
  //     phone: values.mobile,
  //     status: 2,
  //   };
  //   try {
  //     const reponse = client.post(settings.endpoints.createBooking, reqData);
  //   } catch (error) {

  //   }

  // };

  const onSubmit = async (values) => {
    // setApiLoading(true);
    // setApiError(null);
    setSubmitLoading(true);
    const storeId = parseInt(storeID, 10);
    const adult = parseInt(values.guests, 10);
    try {
      if (store?.otp_enable) {
        if (!otpSent) {
          throw new Error("Please send OTP to verify your mobile number");
        }
        const phoneObj = parsePhoneNumber(values.mobile);
        const countryCode = "+" + phoneObj.countryCallingCode;
        const phoneNumber = phoneObj.nationalNumber;
        if (!otpVerified) {
          const otpResp = await postAPI(endpoint, "mobile-verify-otp", {
            mobile_no: phoneNumber,
            country_code: countryCode,
            otp: values.otp,
            came_from: 'Web'
          });
          if (!otpResp?.success) {
            throw new Error("OTP verification failed");
          }
          setOtpVerified(true);
        }
      }

      const bookingDateTime = moment().format("YYYY-MM-DD HH:mm:ss");
      // const userData = st?.userdata;
      const reqData = {
        customer_id: 0,
        first_name: values.firstName,
        last_name: values.lastName,
        booking_datetime: bookingDateTime,
        adult: adult,
        store_id: storeId,
        phone: values.mobile,
        status: 2,
        via: "4",
        is_cover_booking: isCoverBooking,
        city: values.city,
        // cover_per_adult: data?.cover_per_adult,
        // cover_total: data?.cover_total,
        // Phase 1 new fields for cover bookings
        // city: data?.city,
      };

      if (isCoverBooking) {
        const entryFeeValue = (
          coverCharges.find((c) => /entry fee/i.test(c?.title || ""))?.price
        ) || 0;
        const coverChargeValue = (
          coverCharges.find((c) => /cover charge/i.test(c?.title || ""))?.price
        ) || 0;
        reqData.entry_fee = entryFeeValue;
        reqData.cover_charge = coverChargeValue;
        reqData.payment_mode = "";
        reqData.payment_status = "";
      }

      // if (liveEvent) {
      //   data.upcoming_event_id = liveEvent?.id;
      // }
      const resp = await postAPI(endpoint, "online_bookings", reqData);
      // console.log("resp", resp);
      // if (resp?.data?.success) {

      // }
      // setActiveStep(3);
      // orderId = resp.data.id;
      // setOrderNo(resp?.data?.id);
      // setOrderMsg(
      //   "Your booking number is " +
      //     resp?.data?.id +
      //     " We have emailed your booking confirmation, and will send you an update when your booking has been accepted."
      // );
      // setApiLoading(false);
      // showSnackbar({
      //   message:
      //     "Your booking number is " +
      //     resp?.data?.id +
      //     " We have emailed your booking confirmation, and will send you an update when your booking has been accepted.",
      //   severity: "success",
      // });
      // derive pricing details for confirmation view
      const entryFeeValue = (
        coverCharges.find((c) => /entry fee/i.test(c?.title || ""))?.price
      ) || 0;
      const coverChargeValue = (
        coverCharges.find((c) => /cover charge/i.test(c?.title || ""))?.price
      ) || 0;

      let pState = {
        guests: adult,
        bookingId: resp?.data?.id,
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.mobile,
        hideBackToHome: true,
        bookingDateTime: bookingDateTime,
        storeId: storeId,
        customerId: resp?.data?.customer_id ?? 0,
      };
      if (isCoverBooking) {
        pState.entryFee = entryFeeValue;
        pState.coverCharge = coverChargeValue;
        pState.currencySymbol = store?.currency_symbol || "";
      }

      navigate("/booking-confirmation", {
        state: pState,
      });
    } catch (error) {
      // setActiveStep(3);
      console.error("error", error);
      // setApiLoading(false);
      // setOrderMsg(
      //   "There has been some issue with your order. Please try again in some time"
      // );
      showSnackbar({
        message: error?.message || "Failed to book. Please try again.",
        severity: "error",
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      city: "",
      guests: "",
      mobile: "",
      otp: "",
      agreement: false,

    },
    validationSchema,
    onSubmit: onSubmit,
  });

  const handleSendOtp = async () => {
    const { mobile } = formik.values;
    try {
      const phoneObj = parsePhoneNumber(mobile || "");

      // console.log("phoneObj", phoneObj);
      const countryCode = "+" + phoneObj.countryCallingCode.toString();
      // const countryCode = phoneObj.countryCallingCode.toString();
      const phoneNumber = phoneObj.nationalNumber.toString();

      if (!phoneObj?.isValid()) {
        throw new Error("Invalid phone number");
      }

      setOtpLoading(true);
      await postAPI(endpoint, "mobile-otp", {
        mobile_no: phoneNumber,
        otp_type: otpSent ? "resend" : "send",
        country_code: countryCode,
      });
      setOtpSent(true);
      showSnackbar({ message: otpSent ? "OTP resent" : "OTP sent successfully", severity: "success" });
    } catch (err) {
      showSnackbar({
        message: err?.message || "Failed to send OTP",
        severity: "error",
      });
    } finally {
      setOtpLoading(false);
    }
  };

  const handleLookupCustomer = async () => {
    try {
      const phoneObj = parsePhoneNumber(formik.values.mobile || "");
      if (!phoneObj?.isValid()) return;
      const countryCode = "+" + phoneObj.countryCallingCode.toString();
      const phoneNumber = phoneObj.nationalNumber.toString();
      setPrefillLoading(true);
      // Search by phone using text_search as per sample contract
      const resp = await postAPI(endpoint, "admin/search-customer", {
        text_search: `${countryCode}${phoneNumber}`,
      });
      // resp is already response.data per postAPI
      const list = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
      const customer = Array.isArray(list) && list.length > 0 ? list[0] : null;
      if (customer) {
        formik.setFieldValue("firstName", customer.first_name || customer.firstName || formik.values.firstName || "");
        formik.setFieldValue("lastName", customer.last_name || customer.lastName || formik.values.lastName || "");
        formik.setFieldValue("city", customer.city || formik.values.city || "");
      }
    } catch (e) {
      // silent fail - not finding a customer is not an error
    } finally {
      setPrefillLoading(false);
    }
  };

  // reCAPTCHA removed

  if (storeLoading) {
    return (
      <Container maxWidth="lg">
        <CircularProgress variant="indeterminate" />
      </Container>
    );
  }

  if (error || !isObject(storeData) || Object.keys(storeData).length === 0) {
    return <NotFound />;
  }

  return (
    <Container maxWidth="lg">
      <Box
        component="form"
        onSubmit={formik.handleSubmit}
        sx={{ maxWidth: 400, mx: "auto", mt: 4 }}
      >
        <Typography variant="h5" mb={2}>
          {storeData.name}
        </Typography>

        <Typography variant="h6" mb={2}>
          Fast Booking
        </Typography>

        {!coverChargesLoading && !!coverCharges?.length && (
          <Box mb={2} sx={{ p: 2, border: `2px solid ${palette.primary.main}`, borderRadius: 1 }}>
            {/* <Typography variant="subtitle1" gutterBottom>
              Booking Charges Info
            </Typography> */}
            {coverCharges.map((c, idx) => (
              <Box key={idx} sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Typography variant="body2" color={palette.primary.main}>{c.title}</Typography>
                <Typography variant="body2" color={palette.primary.main}>{store?.currency_symbol || ''} {String(c.price)}</Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Mobile Number Field - Moved to Top */}
        {store?.otp_enable ? (
          <>
            <Typography variant="body2" color="primary" sx={{ mt: 2, mb: 1, fontWeight: 500 }}>
              Please verify your number with OTP.
            </Typography>
            <Grid
              container
              spacing={1}
              alignItems={{ xs: "stretch", sm: "center" }}
            >
              <Grid item xs={12} sm={8}>
                <Box>
                  <PhoneNumberInput
                    variant="outlined"
                    value={formik.values.mobile}
                    onChange={(val) => {
                      if (otpVerified) return;
                      formik.setFieldValue("mobile", val || "");
                      if (otpSent) {
                        setOtpSent(false);
                      }
                      if (formik.values.otp) {
                        formik.setFieldValue("otp", "");
                      }
                    }}
                    onBlur={handleLookupCustomer}
                    label="Mobile *"
                    fullWidth
                    disabled={otpVerified}
                    error={
                      formik.touched.mobile && Boolean(formik.errors.mobile)
                    }
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ height: { xs: 48, sm: 56 } }}
                  onClick={handleSendOtp}
                  disabled={
                    otpLoading ||
                    otpVerified ||
                    !isValidPhoneNumber(formik.values.mobile || "")
                  }
                >
                  {otpLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : otpSent && !otpVerified ? (
                    "Resend"
                  ) : (
                    "Send OTP"
                  )}
                </Button>
              </Grid>
            </Grid>
            {formik.touched.mobile && formik.errors.mobile && (
              <FormHelperText error>{formik.errors.mobile}</FormHelperText>
            )}

            {otpSent && (
              <TextField
                fullWidth
                id="otp"
                name="otp"
                label="Enter OTP *"
                value={formik.values.otp}
                onChange={formik.handleChange}
                error={formik.touched.otp && Boolean(formik.errors.otp)}
                helperText={formik.touched.otp && formik.errors.otp}
                margin="normal"
              />
            )}
          </>
        ) : (
          <Box sx={{ mt: 2 }}>
            <PhoneNumberInput
              variant="outlined"
              value={formik.values.mobile}
              onChange={(val) => {
                formik.setFieldValue("mobile", val || "");
                if (otpSent) setOtpSent(false);
                if (otpVerified) setOtpVerified(false);
                if (formik.values.otp) formik.setFieldValue("otp", "");
              }}
              // onBlur={handleLookupCustomer}
              label="Mobile *"
              fullWidth
              disabled={otpVerified}
              error={formik.touched.mobile && Boolean(formik.errors.mobile)}
            />
            {formik.touched.mobile && formik.errors.mobile && (
              <FormHelperText error>{formik.errors.mobile}</FormHelperText>
            )}
          </Box>
        )}

        {prefillLoading && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Fetching your details...
          </Typography>
        )}

        <TextField
          fullWidth
          id="firstName"
          name="firstName"
          label="First Name *"
          type="text"
          value={formik.values.firstName}
          onChange={formik.handleChange}
          error={formik.touched.firstName && Boolean(formik.errors.firstName)}
          helperText={formik.touched.firstName && formik.errors.firstName}
          margin="normal"
        />

        <TextField
          fullWidth
          id="lastName"
          name="lastName"
          label="Last Name (Optional)"
          type="text"
          value={formik.values.lastName}
          onChange={formik.handleChange}
          error={formik.touched.lastName && Boolean(formik.errors.lastName)}
          helperText={formik.touched.lastName && formik.errors.lastName}
          margin="normal"
        />

        {/* Phone First */}
        <FormControl margin="normal" fullWidth>
          <InputLabel>Number of Guests *</InputLabel>
          <Select
            id="guests"
            name="guests"
            value={formik.values.guests}
            onChange={formik.handleChange}
            label="Number of Guests"
            fullWidth
            // MenuProps={{
            //   disableScrollLock: true,
            //   container:
            //     typeof document !== "undefined" ? document.body : undefined,
            // }}
          >
            {Count.map((num) => {
              if (num === 0) return null;
              return (
                <MenuItem key={num} value={num}>
                  {num}
                </MenuItem>
              )
            })}
          </Select>
        </FormControl>

        {/* City */}
        <TextField
          fullWidth
          id="city"
          name="city"
          label="City *"
          type="text"
          value={formik.values.city}
          onChange={formik.handleChange}
          error={formik.touched.city && Boolean(formik.errors.city)}
          helperText={formik.touched.city && formik.errors.city}
          margin="normal"
        />


        <FormControlLabel
          sx={{
            // textAlign: "left",
            width: "100%",
          }}
          margin="normal"
          control={
            <Checkbox
              id="agreement"
              name="agreement"
              color="primary"
              checked={formik.values.agreement}
              onChange={formik.handleChange}
            />
          }
          label={
            <Typography variant="body2">
              I agree to the{" "}
              <Link href={termsURL} target="_blank" rel="noopener noreferrer">
                Terms
              </Link>{" "}
              and{" "}
              <Link href={privacyURL} target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </Link>
            </Typography>
          }
        />
        {formik.touched.agreement && formik.errors.agreement && (
          <FormHelperText error>{formik.errors.agreement}</FormHelperText>
        )}



        
        {/* <Button
          color="primary"
          variant="contained"
          fullWidth
          onClick={() => {
            showSnackbar({
              message: "Failed to send OTP",
              severity: "error",
            });
          }}
        >
          test
        </Button> */}
        <Button
          color="primary"
          variant="contained"
          fullWidth
          type="submit"
          sx={{ mt: 3, height: 60 }}
          loading={formik.isSubmitting}
          disabled={
            formik.isSubmitting || 
            submitLoading ||
            (store?.otp_enable && otpSent && !formik.values.otp)
          }
        >
          {submitLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Submit"
          )}
        </Button>
      </Box>
    </Container>
  );
};

export default SpeedBooking;
